import { Content, Item, Trigger } from '@radix-ui/react-select';
import { styled } from '@sds-system/system';
import type { SizeKey, Theme } from '@sds-system/types';
import {
  getAllStyledProps,
  getBorderStyles,
  getColorStyles,
  getCommonStyles,
  getCSSStyles,
  getFlexStyles,
  getOpacityStyles,
  getShadowStyles,
  getTypographyStyles,
  getZIndexStyles,
  processNestedCSS,
} from '@sds-system/utils';
import type {
  SelectContentStyledProps,
  SelectItemStyledProps,
  SelectSizeKey,
  SelectTriggerStyledProps,
  SelectVariantType,
} from './Select.types';

export const selectIconSizeVariant: Record<SelectVariantType, SizeKey> = {
  outline: '2XS',
  ghost: 'XS',
};

export const selectSizeStyle = (size?: SelectSizeKey) => {
  switch (size) {
    case 'S':
      return {
        padding: '0px 12px',
        minHeight: '32px',
      };
    case 'M':
      return {
        padding: '8px 16px',
        minHeight: '40px',
      };
    case 'L':
      return {
        padding: '12px 16px',
        minHeight: '48px',
      };
    case 'XL':
      return {
        padding: '16px 16px',
        minHeight: '56px',
      };
    default:
      return {
        padding: '8px 16px',
        minHeight: '40px',
      };
  }
};

export const selectGhostSizeStyle = (size: SelectSizeKey) => {
  switch (size) {
    case 'S':
      return {
        paddingLeft: 8,
        paddingRight: 8,
        paddingTop: 4,
        paddingBottom: 4,
        minHeight: 32,
      };
    case 'M':
      return {
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 8,
        paddingBottom: 8,
        minHeight: 40,
      };
    case 'L':
      return {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 12,
        paddingBottom: 12,
        minHeight: 48,
      };
    case 'XL':
      return {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 16,
        paddingBottom: 16,
        minHeight: 56,
      };
  }
};

export const selectFontStyle = (
  theme: Theme,
  size?: SelectSizeKey,
  variant?: SelectVariantType,
) => {
  switch (size) {
    case 'S':
      return {
        ...(variant === 'outline'
          ? theme.typography.body2.regular
          : theme.typography.caption.regular),
      };
    case 'M':
      return {
        ...theme.typography.body2.regular,
      };
    case 'L':
      return {
        ...(variant === 'outline' ? theme.typography.body2.regular : theme.typography.body.regular),
      };
    case 'XL':
      return {
        ...theme.typography.body.regular,
      };
    default:
      return {
        ...theme.typography.body2.regular,
      };
  }
};

export const selectItemFontStyle = (
  theme: Theme,
  size?: SelectSizeKey,
  variant?: SelectVariantType,
) => {
  switch (size) {
    case 'M':
      return {
        ...theme.typography.body2.regular,
      };
    case 'L':
      return {
        ...(variant === 'outline' ? theme.typography.body2.regular : theme.typography.body.regular),
      };
    case 'XL':
      return {
        ...theme.typography.body.regular,
      };
    default:
      return {
        ...theme.typography.body2.regular,
      };
  }
};

export const StyledSelectTrigger = styled(Trigger, {
  componentName: 'Select',
  slot: 'trigger',
})<SelectTriggerStyledProps>(
  ({ theme, size = 'M', width, variant }) => {
    return {
      display: 'inline-flex',
      justifyContent: 'space-between',
      gap: 4,
      alignItems: 'center',
      backgroundColor: theme.palette.bg.primary,
      pointerEvents: 'auto',
      outline: 'none',
      width: width || '100%',
      textAlign: 'left',
      ...(size && selectFontStyle(theme, size, variant)),
    };
  },
  ({ theme, isError, size = 'M' }) => ({
    variants: [
      {
        props: { variant: 'outline' },
        style: {
          ...selectSizeStyle(size),
          gap: 4,
          border: '1px solid',
          borderColor: isError ? theme.palette.border.error : theme.palette.border.secondary,
          borderRadius: theme.borderRadius[8],
          '&:disabled': processNestedCSS(
            {
              border: '1px solid',
              borderColor: theme.palette.border.disabled,
              backgroundColor: theme.palette.bg.disabledLight,
              color: theme.palette.text.disabled,
            },
            theme,
          ),
        },
      },
      {
        props: { variant: 'ghost' },
        style: {
          ...selectGhostSizeStyle(size),
          gap: 4,
          border: 'none',
          height: '32px !important',
          '&:disabled': processNestedCSS(
            {
              color: theme.palette.icon.disabled,
            },
            theme,
          ),
        },
      },
    ],
  }),
  getAllStyledProps,
);

export const StyledSelectContent = styled(Content, {
  componentName: 'Select',
  slot: 'content',
})<SelectContentStyledProps>(
  ({ theme }) => {
    return {
      minWidth: `var(--radix-select-trigger-width)`,
      maxHeight: `var(--radix-select-content-available-height)`,
      height: 'auto',
      zIndex: theme.zIndex['9999'],
      overflow: 'hidden',
      pointerEvents: 'auto',
      borderRadius: theme.borderRadius[8],
      border: '1px solid',
      borderColor: theme.palette.border.secondary,
    };
  },
  getCommonStyles,
  getBorderStyles,
  getColorStyles,
  getOpacityStyles,
  getShadowStyles,
  getTypographyStyles,
  getFlexStyles,
  getZIndexStyles,
  getCSSStyles,
);

export const StyledSelectItem = styled(Item, {
  componentName: 'Select',
  slot: 'item',
})<SelectItemStyledProps>(({ theme, size, variant }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 'var(--radix-select-trigger-width)',
    padding: '0 16px',
    outline: 'none',
    backgroundColor: theme.palette.bg.primary,
    pointerEvents: 'auto',
    cursor: 'pointer',
    ...(size && selectSizeStyle(size)),
    ...(size && selectItemFontStyle(theme, size, variant)),
    '&:hover': processNestedCSS(
      {
        backgroundColor: theme.palette.bg.interactive.selected,
      },
      theme,
    ),

    variants: [
      {
        props: { variant: 'outline' },
        style: {
          '&:first-of-type': processNestedCSS(
            {
              borderRadius: `${theme.borderRadius[8]}px ${theme.borderRadius[8]}px 0 0`,
            },
            theme,
          ),
          '&:last-child': processNestedCSS(
            {
              borderRadius: `0 0 ${theme.borderRadius[8]}px ${theme.borderRadius[8]}px`,
            },
            theme,
          ),
        },
      },
    ],
  };
});
